import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Home, Briefcase, ArrowRight } from "lucide-react";
import DebtNavbar from "../components/layout/DebtNavbar";
import Seo from "../components/Seo";
import { useAuth } from "../lib/AuthContext";
import api, { formatApiError } from "../lib/api";

/**
 * Post-signup 30-second segmentation for borrowers.
 *
 * Sits between `/money/signup` and `/money`. Asks Individual
 * vs Business — the answer persists on the user record
 * (`users.debt_audience`) and forwards to `/money?audience=…&prefill=1`
 * so the Products tab opens correctly. If the user already answered, we
 * short-circuit and forward straight to the landing page.
 */
export default function RaiseDebtOnboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [saving, setSaving] = useState(null);
  const [checking, setChecking] = useState(true);

  // Short-circuit if the user already answered before.
  useEffect(() => {
    if (!user) { setChecking(false); return; }
    api.get("/debt/onboard/audience")
      .then((r) => {
        const a = r.data?.debt_audience;
        if (a === "individual" || a === "business") {
          nav(`/money?audience=${a}&prefill=1`, { replace: true });
          return;
        }
        setChecking(false);
      })
      .catch(() => setChecking(false));
  }, [user, nav]);

  const pick = async (audience) => {
    setSaving(audience);
    try {
      await api.post("/debt/onboard/audience", { audience });
      nav(`/money?audience=${audience}&prefill=1`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't save your choice"));
      setSaving(null);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-cream text-ink">
        <DebtNavbar />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="text-sm text-ink-muted">Loading your profile…</div>
        </div>
      </div>
    );
  }

  const CARDS = [
    {
      key: "individual",
      title: "I'm an Individual",
      body: "Home loan, car loan, personal loan, education loan, wedding, medical, gold, LAP. We compare offers from 85+ banks & NBFCs and negotiate the best rate for you.",
      icon: Home,
    },
    {
      key: "business",
      title: "I represent a Business",
      body: "Working capital, term loan, MSME unsecured, invoice discounting, LAP, RBF, structured credit, bridge finance — up to ₹500 Cr. Bank-style personal discussion, no bot loops.",
      icon: Briefcase,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FBFAF5] via-[#F5F1E8] to-[#E8DFC9] text-ink" data-testid="raise-debt-onboard">
      <Seo title="Quick question — Individual or Business? | VenturaZ Money" description="One 5-second question so we can shortlist the right loan products for you." path="/money/onboard" noindex />
      <Toaster richColors position="top-right" />
      <DebtNavbar />
      <section className="max-w-3xl mx-auto px-6 py-14 lg:py-20">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-2">STEP 2 OF 2 · TAKES 5 SECONDS</div>
        <h1 className="font-display text-3xl md:text-4xl leading-tight mb-3" data-testid="raise-debt-onboard-heading">
          Nice to meet you, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="text-ink-soft text-base leading-relaxed max-w-xl mb-8">
          One tiny question so we can shortlist the right loan products and lenders for you. Change it anytime from your profile later.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CARDS.map((c) => {
            const Icon = c.icon;
            const active = saving === c.key;
            return (
              <button
                key={c.key}
                type="button"
                disabled={!!saving}
                onClick={() => pick(c.key)}
                className={`group text-left border-2 rounded-2xl p-6 bg-white transition-all disabled:opacity-70 disabled:cursor-not-allowed ${
                  active
                    ? "border-[#7A4E1D] shadow-[0_20px_40px_rgba(122,78,29,0.18)]"
                    : "border-[#B8860B]/30 hover:border-[#7A4E1D] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(122,78,29,0.12)]"
                }`}
                data-testid={`raise-debt-onboard-${c.key}`}
              >
                <div className="w-11 h-11 rounded-lg bg-[#7A4E1D] text-[#FFF3D9] inline-flex items-center justify-center mb-4">
                  <Icon size={20} />
                </div>
                <div className="font-heading text-ink text-lg mb-2">{c.title}</div>
                <div className="text-[13px] text-ink-soft leading-relaxed mb-4">{c.body}</div>
                <div className="inline-flex items-center gap-1 text-sm font-heading text-[#7A4E1D] group-hover:underline">
                  {active ? "Saving…" : "Continue"} <ArrowRight size={14} />
                </div>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => nav("/money?prefill=1", { replace: true })}
          className="mt-6 text-xs text-ink-muted hover:text-[#7A4E1D] hover:underline"
          data-testid="raise-debt-onboard-skip"
        >
          Skip for now — I&apos;ll pick later
        </button>
      </section>
    </div>
  );
}
